<template>
  <div>
    <h1 class="mb-8 font-bold text-3xl text-center">Dokumentexport</h1>
    <div class="w-full bg-white rounded-lg shadow p-8 mx-auto">
      <!-- Grid for client info and base template selection -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div>
          <h2 class="text-2xl font-semibold mb-4 border-b pb-2">Kundeninformation</h2>
          <div class="space-y-2">
            <div>
              <strong>Name:</strong> {{ client.first_name }} {{ client.last_name }}
            </div>
            <div>
              <strong>Email:</strong> {{ client.email }}
            </div>
            <div>
              <strong>Telefon:</strong> {{ client.phone }}
            </div>
          </div>
        </div>
        <div class="flex flex-col justify-between">
          <select-input v-model="docBaseType" label="Basisvorlage wählen" class="mb-4">
            <option
              v-for="type in [{ key: 'without_logo', value: 'Ohne Logo' }, { key: 'with_logo', value: 'Mit Logo' }]"
              :key="type.key"
              :value="type.key"
            >
              {{ type.value }}
            </option>
          </select-input>
          <text-input v-model="documentLocation" label="Ort eingeben" />
        </div>
      </div>

      <!-- Grid for header and text selection -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div>
          <h2 class="text-2xl font-semibold mb-4 border-b pb-2">Kopfzeile auswählen</h2>
          <select-input v-model="selectedHeader" class="w-full mb-4">
            <option v-for="header in headers" :key="header.id" :value="header.text">
              {{ header.text | stripHTML }}
            </option>
          </select-input>
          <textarea-input
            ref="myQuillEditor"
            v-model="selectedHeaderInput"
            class="w-full"
            label="Text"
            placeholder="Oder benutzerdefinierten Kopftext eingeben"
            rows="15"
          />
        </div>

        <div>
          <h2 class="text-2xl font-semibold mb-4 border-b pb-2">Text auswählen</h2>
          <select-input v-model="selectedText" class="w-full mb-4">
            <option v-for="text in texts" :key="text.id" :value="text.text">
              {{ text.text | stripHTML }}
            </option>
          </select-input>
          <textarea-input
            v-model="selectedTextInput"
            class="w-full"
            label="Text"
            placeholder="Oder benutzerdefinierten Text hier eingeben"
            rows="15"
          />
        </div>
      </div>

      <div class="px-8 py-4 bg-gray-50 border-t border-gray-200 flex justify-end rounded-b-lg">
        <button
          class="btn-green hover:bg-green-700 text-white font-bold py-3 px-6 rounded shadow transition"
          @click="generateDocument"
        >
          Generieren
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import { docTypes } from '../Documents/docTypes'
import SelectInput from '../../Shared/SelectInput.vue'
import TextInput from '../../Shared/TextInput.vue'
import TextareaInput from '../../Shared/TextareaInput.vue'

export default {
  metaInfo: { title: 'Dokumente' },
  components: {
    TextareaInput,
    TextInput,
    SelectInput,
  },
  filters: {
    stripHTML: function (string) {
      return string ? string.replace(/<\/?[^>]+>|&nbsp;/ig, ' ') : string
    },
  },
  layout: Layout,
  props: {
    client: Object,
    headers: Array,
    texts: Array,
  },
  data() {
    return {
      selectedHeader: null,
      selectedText: null,
      selectedHeaderInput: null,
      selectedTextInput: null,
      documentLocation: 'Düsseldorf',
      docBaseType: 'without_logo',
    }
  },
  watch: {
    selectedHeader(newValue) {
      if (!newValue) return
      // Define the placeholders and their replacement values.
      const placeholders = {
        '[last_name]': this.client.last_name || '',
        '[first_name]': this.client.first_name || '',
        '[gender]': this.client.gender || '',
        '[credit_amount]': this.client.wanted_credit_amount
          ? parseFloat(this.client.wanted_credit_amount).toLocaleString('de-DE', { minimumFractionDigits: 2 })
          : '',
      }

      // Replace placeholders with corresponding values.
      this.selectedHeaderInput = newValue.replace(
        /\[last_name]|\[first_name]|\[gender]|\[credit_amount]/g,
        (placeholder) => placeholders[placeholder] || placeholder,
      )

      if(newValue.indexOf('[gender]') !== -1) {
        if(this.client.gender) {
          this.selectedHeaderInput = this.selectedHeaderInput.replaceAll('geehrte(r)', 'geehrte')
        }
        if(this.client.gender === 'Frau') {
          if(this.selectedHeaderInput.includes('geehrter')) {
            this.selectedHeaderInput = this.selectedHeaderInput.replaceAll('geehrter', 'geehrte')
          }
        }
        if(this.client.gender === 'Herr') {
          if (this.selectedHeaderInput.includes('geehrte ')) {
            this.selectedHeaderInput = this.selectedHeaderInput.replaceAll('geehrte', 'geehrter')
          }
        }
      }
    },
    selectedText(newValue) {
      if (!newValue) return
      if (!newValue) return
      // Define the placeholders and their replacement values.
      const placeholders = {
        '[last_name]': this.client.last_name || '',
        '[first_name]': this.client.first_name || '',
        '[gender]': this.client.gender || '',
        '[credit_amount]': this.client.wanted_credit_amount
          ? parseFloat(this.client.wanted_credit_amount).toLocaleString('de-DE', { minimumFractionDigits: 2 })
          : '',
      }

      // Replace placeholders with corresponding values.
      this.selectedTextInput = newValue.replace(
        /\[last_name]|\[first_name]|\[gender]|\[credit_amount]/g,
        (placeholder) => placeholders[placeholder] || placeholder,
      )

      if(newValue.indexOf(placeholders['[gender]']) !== -1 && !this.client.gender) {
        alert('Bitte Geschlecht des Kunden angeben, um das Dokument zu generieren.')
      }

      if(newValue.indexOf('[gender]') !== -1) {
        if(this.client.gender) {
          this.selectedTextInput = this.selectedTextInput.replaceAll('geehrte(r)', 'geehrte')
        }
        if(this.client.gender === 'Frau') {
          if(this.selectedTextInput.includes('geehrter')) {
            this.selectedTextInput = this.selectedTextInput.replaceAll('geehrter', 'geehrte')
          }
        }
        if(this.client.gender === 'Herr') {
          if (this.selectedTextInput.includes('geehrte ')) {
            this.selectedTextInput = this.selectedTextInput.replaceAll('geehrte', 'geehrter')
          }
        }
      }
    },
  },
  methods: {
    docTypes() {
      return docTypes
    },
    generateDocument() {
      // if header or text is not selected, show alert
      if (!this.selectedHeader || !this.selectedText || !this.selectedHeaderInput || !this.selectedTextInput) {
        alert('Bitte wählen Sie eine Kopfzeile oder einen Text aus, um das Dokument zu generieren.')
        return
      }
      // if selectedHeaderInput or selectedTextInput still contains placeholders, show alert
      if (this.selectedHeaderInput.includes('[last_name]')
        || this.selectedHeaderInput.includes('[first_name]')
        || this.selectedHeaderInput.includes('[gender]')
        || this.selectedTextInput.includes('[last_name]')
        || this.selectedTextInput.includes('[first_name]')
        || this.selectedTextInput.includes('[gender]')
        || this.selectedTextInput.includes('[credit_amount]')
      ) {
        alert('Bitte ersetzen Sie alle Platzhalter (tags mit []).')
        return
      }
      const documentData = {
        is_doc_logo: this.docBaseType === 'with_logo',
        gender: this.client.gender,
        first_name: this.client.first_name,
        last_name: this.client.last_name,
        address: this.client.address,
        house_number: this.client.house_number,
        zip_code: this.client.zip_code,
        city: this.client.city,
        country: this.client.country,
        credit_amount: this.client.wanted_credit_amount,
        header: this.selectedHeaderInput || '',
        text: this.selectedTextInput || '',
        document_city: this.documentLocation || '',
      }

      fetch(this.route('documents.generate').url(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(documentData),
      })
        .then(response => response.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `${this.client.first_name}_${this.client.last_name}_dokument.docx`
          document.body.appendChild(a)
          a.click()
          a.remove()
        })
        .catch(error => {
          console.error('Fehler beim Generieren des Dokuments:', error)
        })
    },
  },
}
</script>
